import type { Privilege } from '@/lib/privilege'
import type { IMember, MemberGroup } from '@/api/members/types'
import { Lang } from '@/lang/types'
import { useI18n } from 'vue-i18n'

/**
 * 判別目前會員是否有特定權限
 * @param content_code 權限代碼，若為陣列則必須全部皆符合
 *
 * @example
 *
 * import { Privilege } from '@/lib/privilege'
 *
 * <div v-if="usePrivilege(Privilege.Assets_Inventory)">
 *   目前庫存
 * </div>
 *
 */
export function usePrivilege(content_code: Privilege | Privilege[] | MemberGroup | MemberGroup[] | 'member' | Lang, member?: IMember | null): boolean {
  // 如果是陣列，必須符合所有權限
  if (content_code instanceof Array) {
    return content_code.every(c => usePrivilege(c))
  }

  member = member ?? useMember().value

  if (member && isPrivilege(content_code)) {
    return ((member.privileges ?? []).includes(content_code))
  }

  if (member && isMemberGroup(content_code)) {
    return member.group === content_code
  }

  if (isLang(content_code)) {
    const { locale } = useI18n()
    return locale.value === content_code
  }

  if (content_code === 'member') {
    return !!member
  } else {
    return false
  }
}

/**
 * 檢查是否符合任一個權限項目
 * @param content_codes 需要的任何權限陣列
 * @returns 是否符合其中之一
 */
export function hasAnyPrivilege(content_codes: Privilege[]): boolean {
  return content_codes.some(c => usePrivilege(c))
}
